import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout';
import SuperLayout from './components/SuperLayout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Classes from './pages/Classes';
import Attendance from './pages/Attendance';
import Lessons from './pages/Lessons';
import Tests from './pages/Tests';
import Tuition from './pages/Tuition';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Teachers from './pages/Teachers';
import Staff from './pages/Staff';
import Counseling from './pages/Counseling';
import TeacherMonitoring from './pages/TeacherMonitoring';
import StaffNotice from './pages/StaffNotice';
import ParentNotice from './pages/ParentNotice';
import WeeklyReports from './pages/WeeklyReports';
import MonthlyReports from './pages/MonthlyReports';
import Chat from './pages/Chat';
import Points from './pages/Points';
import PointsHistory from './pages/PointsHistory';
import PointsUsage from './pages/PointsUsage';
import PointsCharge from './pages/PointsCharge';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFail from './pages/PaymentFail';
import PlanSubscribe from './pages/PlanSubscribe';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionManage from './pages/SubscriptionManage';
import SubscriptionPayments from './pages/SubscriptionPayments';
import Billing from './pages/Billing';
import BillingDetail from './pages/BillingDetail';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
// Super Admin Pages
import SuperLogin from './pages/super/SuperLogin';
import SuperDashboard from './pages/super/SuperDashboard';
import SuperTenants from './pages/super/SuperTenants';
import SuperCharges from './pages/super/SuperCharges';
// Public Pages
import PublicLessonView from './pages/public/PublicLessonView';
import PublicWeeklyReport from './pages/public/PublicWeeklyReport';
import PublicMonthlyReport from './pages/public/PublicMonthlyReport';
import PublicNotice from './pages/public/PublicNotice';
import PublicSchedule from './pages/public/PublicSchedule';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">로딩 중...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route (redirect if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">로딩 중...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Home Route (Landing or Dashboard)
function HomeRoute() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">로딩 중...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Landing />;
}

// Super Admin Protected Route
function SuperProtectedRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">로딩 중...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/super/login" replace />;
  }

  if (user?.role !== 'super') {
    return <Navigate to="/super/login" replace />;
  }

  return <SuperLayout>{children}</SuperLayout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Web Fallback Pages (no auth required) */}
      <Route path="/:tenantId/lessons/:id" element={<PublicLessonView />} />
      <Route path="/:tenantId/reports/weekly/:id" element={<PublicWeeklyReport />} />
      <Route path="/:tenantId/reports/monthly/:id" element={<PublicMonthlyReport />} />
      <Route path="/:tenantId/notice/:id" element={<PublicNotice />} />
      <Route path="/:tenantId/schedule/:id" element={<PublicSchedule />} />

      <Route path="/login" element={
        <PublicRoute><Login /></PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute><Register /></PublicRoute>
      } />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/students" element={
        <ProtectedRoute><Students /></ProtectedRoute>
      } />
      <Route path="/classes" element={
        <ProtectedRoute><Classes /></ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute><Attendance /></ProtectedRoute>
      } />
      <Route path="/lessons" element={
        <ProtectedRoute><Lessons /></ProtectedRoute>
      } />
      <Route path="/tests" element={
        <ProtectedRoute><Tests /></ProtectedRoute>
      } />
      <Route path="/tuition" element={
        <ProtectedRoute><Tuition /></ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute><Notifications /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/teachers" element={
        <ProtectedRoute><Teachers /></ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute><Staff /></ProtectedRoute>
      } />
      <Route path="/counseling" element={
        <ProtectedRoute><Counseling /></ProtectedRoute>
      } />
      <Route path="/teacher-monitoring" element={
        <ProtectedRoute><TeacherMonitoring /></ProtectedRoute>
      } />
      <Route path="/staff-notice" element={
        <ProtectedRoute><StaffNotice /></ProtectedRoute>
      } />
      <Route path="/parent-notice" element={
        <ProtectedRoute><ParentNotice /></ProtectedRoute>
      } />
      <Route path="/weekly-reports" element={
        <ProtectedRoute><WeeklyReports /></ProtectedRoute>
      } />
      <Route path="/monthly-reports" element={
        <ProtectedRoute><MonthlyReports /></ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute><Chat /></ProtectedRoute>
      } />
      <Route path="/points" element={
        <ProtectedRoute><Points /></ProtectedRoute>
      } />
      <Route path="/points/history" element={
        <ProtectedRoute><PointsHistory /></ProtectedRoute>
      } />
      <Route path="/points/usage" element={
        <ProtectedRoute><PointsUsage /></ProtectedRoute>
      } />
      <Route path="/points/charge" element={
        <ProtectedRoute><PointsCharge /></ProtectedRoute>
      } />
      <Route path="/payments/success" element={
        <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
      } />
      <Route path="/payments/fail" element={
        <ProtectedRoute><PaymentFail /></ProtectedRoute>
      } />
      <Route path="/subscription/plans" element={
        <ProtectedRoute><PlanSubscribe /></ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>
      } />
      <Route path="/subscription/manage" element={
        <ProtectedRoute><SubscriptionManage /></ProtectedRoute>
      } />
      <Route path="/subscription/payments" element={
        <ProtectedRoute><SubscriptionPayments /></ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute><Billing /></ProtectedRoute>
      } />
      <Route path="/billing/:id" element={
        <ProtectedRoute><BillingDetail /></ProtectedRoute>
      } />

      {/* Super Admin Routes */}
      <Route path="/super/login" element={<SuperLogin />} />
      <Route path="/super" element={
        <SuperProtectedRoute><SuperDashboard /></SuperProtectedRoute>
      } />
      <Route path="/super/tenants" element={
        <SuperProtectedRoute><SuperTenants /></SuperProtectedRoute>
      } />
      <Route path="/super/charges" element={
        <SuperProtectedRoute><SuperCharges /></SuperProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
